import { z } from "zod";

import { COUNTRIES } from "./countries";
import type { FinanceAuditEvent } from "./reconciliation";

// Payees: the bank account each vendor is paid at, which an editor of Finance keeps on the vendor's page, and which a
// bill from that vendor is paid to. It is the organization's own record of where its money goes — never what a bill
// prints, which a forged or altered one could change — so only an editor of Finance saves it or removes it on its own
// (removing the vendor from the Directory forgets it too), only a member who can read Finance reads it, and every
// change is recorded. It is kept apart from the vendor's record, which the Directory, its knowledge base, the
// integration API, its MCP server and the Directory agent all read — and all but the knowledge base write. The API
// and the frontend share these.

/** Whom a payment goes to, as a bank names them: a company, or a person by their first and last name. */
export const PAYEE_TYPES = ["company", "individual"] as const;
export type PayeeType = (typeof PAYEE_TYPES)[number];

export const PAYEE_TYPE_LABELS: Readonly<Record<PayeeType, string>> = {
	company: "Company",
	individual: "Person",
};

/** `text` without spaces or dashes, in capitals: how an IBAN, a BIC, an account number or a code is kept. */
function compacted(text: string): string {
	return text.replaceAll(/[\s-]/gu, "").toUpperCase();
}

/**
 * Whether an IBAN's check digits are right (ISO 13616): its first four characters moved to its end, each letter read
 * as a number from 10 to 35, the whole is 1 modulo 97 — worked a few digits at a time, as no number holds it whole.
 */
export function hasValidIbanChecksum(iban: string): boolean {
	const rearranged = `${iban.slice(4)}${iban.slice(0, 4)}`;
	let remainder = 0;
	for (const character of rearranged) {
		remainder = Number(`${String(remainder)}${String(Number.parseInt(character, 36))}`) % 97;
	}
	return remainder === 1;
}

/** An IBAN, spaces and dashes taken out and in capitals, whose check digits are right. */
export const ibanSchema = z
	.string()
	.transform(compacted)
	.pipe(
		z
			.string()
			.regex(/^[A-Z]{2}\d{2}[A-Z\d]{11,30}$/u, "Enter a valid IBAN")
			.refine(hasValidIbanChecksum, "Check the IBAN: its check digits do not match"),
	);

/** A BIC (SWIFT code): a bank's four letters, its country's two, two of its place and, for a branch, three more. */
export const bicSchema = z
	.string()
	.transform(compacted)
	.pipe(z.string().regex(/^[A-Z]{6}[A-Z\d]{2}(?:[A-Z\d]{3})?$/u, "Enter an 8- or 11-character BIC"));

/**
 * An account number outside the IBAN countries, or in the United Kingdom and the United States, as its bank writes it.
 */
export const accountNumberSchema = z
	.string()
	.transform(compacted)
	.pipe(z.string().regex(/^[A-Z\d]{4,34}$/u, "Enter the account number"));

/** A UK sort code: six digits, written with or without its dashes. */
export const sortCodeSchema = z
	.string()
	.transform(compacted)
	.pipe(z.string().regex(/^\d{6}$/u, "Enter a 6-digit sort code"));

/** A US routing number (ABA): nine digits. */
export const routingNumberSchema = z
	.string()
	.transform(compacted)
	.pipe(z.string().regex(/^\d{9}$/u, "Enter a 9-digit routing number"));

/** A line of an address as a bank takes it: at most 50 characters. */
const addressLine = z.string().trim().max(50, "Use at most 50 characters");

/**
 * Where an account's holder is, which a bank asks for when a payment leaves the euro area or a country's own clearing:
 * the street, the city, the postal code and the country, and the state or province where a country has them.
 */
export const payeeAddressSchema = z.strictObject({
	line1: addressLine.min(1, "Enter the street and number"),
	line2: addressLine.min(1).optional(),
	city: addressLine.min(1, "Enter the city"),
	postalCode: addressLine.min(1, "Enter the postal code"),
	region: addressLine.min(1).optional(),
	country: z.enum(COUNTRIES, "Choose the country"),
});
export type PayeeAddress = z.infer<typeof payeeAddressSchema>;

/** The fields of a bank account, before the rules that span them. */
const bankAccountFields = {
	/** Who holds the account, as the bank names them: a company's name, or a person's first and last name. */
	holderName: z.string().trim().min(1, "Enter the account holder's name").max(80, "Use at most 80 characters"),
	payeeType: z.enum(PAYEE_TYPES, "Choose a company or a person"),
	/** Where the bank is, which decides which of the codes below a payment needs. */
	bankCountry: z.enum(COUNTRIES, "Choose the bank's country"),
	iban: ibanSchema.optional(),
	bic: bicSchema.optional(),
	accountNumber: accountNumberSchema.optional(),
	sortCode: sortCodeSchema.optional(),
	routingNumber: routingNumberSchema.optional(),
	address: payeeAddressSchema.optional(),
};

/** What the rules that span a bank account's fields read. */
interface BankAccountShape {
	readonly holderName: string;
	readonly payeeType: PayeeType;
	readonly bankCountry: string;
	readonly iban?: string;
	readonly bic?: string;
	readonly accountNumber?: string;
	readonly sortCode?: string;
	readonly routingNumber?: string;
}

/**
 * The rules that span a bank account's fields: an IBAN or an account number, one of the two; an IBAN of the bank's own
 * country; a UK account's sort code and a US account's routing number; a BIC for any other account, IBANs included; and
 * a person named by their first and last name, as a bank pays one.
 */
function checkBankAccount(account: BankAccountShape, context: z.RefinementCtx): void {
	const { holderName, payeeType, bankCountry, iban, bic, accountNumber, sortCode, routingNumber } = account;
	if ((iban === undefined) === (accountNumber === undefined)) {
		context.addIssue({
			code: "custom",
			path: ["iban"],
			message: "Enter an IBAN or an account number, one of the two",
		});
	}
	if (iban !== undefined && !iban.startsWith(bankCountry)) {
		context.addIssue({ code: "custom", path: ["iban"], message: "This IBAN is of another country than the bank" });
	}
	const ukOrUsAccount = accountNumber !== undefined && (bankCountry === "GB" || bankCountry === "US");
	if (!ukOrUsAccount && bic === undefined) {
		context.addIssue({ code: "custom", path: ["bic"], message: "Enter the bank's BIC" });
	}
	if (accountNumber !== undefined && bankCountry === "GB" && sortCode === undefined) {
		context.addIssue({ code: "custom", path: ["sortCode"], message: "Enter the sort code" });
	}
	if (accountNumber !== undefined && bankCountry === "US" && routingNumber === undefined) {
		context.addIssue({ code: "custom", path: ["routingNumber"], message: "Enter the routing number" });
	}
	if (payeeType === "individual" && holderName.trim().split(/\s+/u).length < 2) {
		context.addIssue({ code: "custom", path: ["holderName"], message: "Enter a first and a last name" });
	}
}

/**
 * A vendor's bank account as it was saved and as the API answers it: each field in its own form, spaces and dashes
 * taken out of the codes, which are kept in capitals. The rules that span the fields are what a request is held to
 * (`bankAccountSchema`), never what is read back, so a rule made stricter later leaves every account saved before it
 * readable.
 */
export const savedBankAccountSchema = z.strictObject(bankAccountFields);
export type BankAccount = z.output<typeof savedBankAccountSchema>;

/**
 * A vendor's bank account, as `PUT /api/payments/payees/{vendorId}` takes it: who holds it, where its bank is, and
 * either an IBAN — with the bank's BIC — or a local account number with the code its country routes payments by: a UK
 * sort code, a US routing number, or a BIC anywhere else; and the holder's address, which a payment out of the euro
 * area or a country's own clearing needs.
 */
export const bankAccountSchema = savedBankAccountSchema.superRefine(checkBankAccount);
export type BankAccountRequest = z.input<typeof bankAccountSchema>;

/** A vendor's bank account as the API keeps it: the vendor it pays, and who last changed it and when. */
export const payeeSchema = z.strictObject({
	vendorId: z.string().min(1).max(128),
	account: savedBankAccountSchema,
	updatedBy: z.string().min(1).max(80),
	/** When, as an ISO 8601 timestamp in UTC. */
	updatedAt: z.iso.datetime(),
});
export type Payee = z.infer<typeof payeeSchema>;

/**
 * What `GET` and `PUT /api/payments/payees/{vendorId}` answer: the vendor's bank account, or null while it has none.
 */
export const payeeAnswerSchema = z.strictObject({ payee: payeeSchema.nullable() });
export type PayeeAnswer = z.infer<typeof payeeAnswerSchema>;

/**
 * "IBAN ending 5678", "Account ending 4321": a bank account named by the last four characters of its number, which is
 * all of it that an audit entry or a list ever names — and by none of them when an account number is shorter than
 * eight characters, whose last four would come close to all of it: "Short account number". An IBAN is never shorter
 * than fifteen.
 */
export function bankAccountHint({ iban, accountNumber }: Pick<BankAccount, "iban" | "accountNumber">): string {
	if (iban !== undefined) {
		return `IBAN ending ${iban.slice(-4)}`;
	}
	const code = accountNumber ?? "";
	return code.length < 8 ? "Short account number" : `Account ending ${code.slice(-4)}`;
}

/**
 * "Bank account added" / "Vantage Cloud · IBAN ending 5678", or "Bank account changed" / "Vantage Cloud · IBAN ending
 * 3000, was IBAN ending 5678" — or just the account, when its number stayed and something else changed: what became
 * of a vendor's bank account, named by its last four characters alone.
 */
export function bankAccountSavedAudit(
	vendorName: string,
	account: BankAccount,
	previous: BankAccount | undefined,
): FinanceAuditEvent {
	const hint = bankAccountHint(account);
	if (previous === undefined) {
		return { action: "Bank account added", detail: `${vendorName} · ${hint}` };
	}
	const was = bankAccountHint(previous);
	return { action: "Bank account changed", detail: `${vendorName} · ${hint}${was === hint ? "" : `, was ${was}`}` };
}

/** "Bank account removed" / "Vantage Cloud · IBAN ending 5678". */
export function bankAccountRemovedAudit(vendorName: string, account: BankAccount): FinanceAuditEvent {
	return { action: "Bank account removed", detail: `${vendorName} · ${bankAccountHint(account)}` };
}
